import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { MemberPhotoSchema } from '../../profiles/member-view/member-view';
import { AccountRefSchema } from '../../users/account-ref/account-ref';
import { GenderSchema } from '../../users/gender';

import { AnswerChangeSchema } from '../answer-change/answer-change';
import { FaceMatchCheckSchema } from '../face-match/face-match';
import { InterviewFeeStandingSchema } from '../interview-access/interview-access';
import { InterviewSchema } from '../interview/interview';
import { PhotoAdvisorySchema } from '../photo-advisory/photo-advisory';
import { VerificationCaseKindSchema } from '../verification-case-kind/verification-case-kind';
import { VerificationCaseStatusSchema } from '../verification-case-status/verification-case-status';
import { refineCaseContents, VerificationStepSchema } from '../verification-case/verification-case';
import { VerificationDecisionReasonSchema } from '../verification-decision/verification-decision';

/**
 * One row of the review queue.
 *
 * A pointer, not a document: no steps, no evidence, no answers. Everything a
 * reviewer needs to *choose* a case, and nothing that would put ID photographs
 * into a list response — a queue render is not an authorized view of somebody's
 * passport, and N12 audits every one of those.
 *
 * `gender` is here because the queue is filtered by it when
 * `verification.genderMatchedReview` is on (a marketable trust feature), and a
 * client that had to fetch each case to learn it would defeat the filter.
 *
 * `account` is here for a blunter reason: **a uuid is not a person.** The
 * reviewer has to recognise whose case they are picking up, and the client
 * cannot look it up itself — `GET /admin/users` answers to `members.read`, which
 * a reviewer holding only `verification.review` need not have, so a client-side
 * resolve would paint a page of bare ids for exactly the operator this screen is
 * for. It is resolved server-side over the batched QueryBus instead, one round
 * trip per page, the way the Trust queue does it (P14b).
 */
export const VerificationCaseSummarySchema = z
  .object({
    id: UuidSchema,
    userId: UuidSchema,
    /**
     * Who the member is — or `null` when the lookup came back short.
     *
     * Nullable rather than required, and that is not defensive: an id that
     * resolves to nothing is **absent** from the account-ref map, and the row is
     * still a row a reviewer must be able to see. A required field here would
     * throw inside the mapper and blank the whole page over one account (P14b).
     */
    account: AccountRefSchema.nullable(),
    gender: GenderSchema,
    kind: VerificationCaseKindSchema,
    status: VerificationCaseStatusSchema,
    attemptNumber: z.number().int().positive(),
    /** Steps still waiting on somebody — what sorts the reviewer's attention. */
    outstandingSteps: z.number().int().nonnegative(),
    /** How many edited answers a re-review holds; 0 on an initial case (D6). */
    answerChangeCount: z.number().int().nonnegative(),
    /** When the live appointment is held; `null` when none is booked — what the interviews list is ordered by (F7). */
    interviewAt: z.iso.datetime().nullable(),
    submittedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
  })
  .strict();

export type VerificationCaseSummary = z.infer<typeof VerificationCaseSummarySchema>;

/**
 * The case a reviewer opens.
 *
 * It is the member's shape **plus** what only staff may see — who decided it,
 * and the private interview notes — and it deliberately reuses
 * `VerificationStepSchema` rather than declaring a staff copy: a second step
 * shape is exactly where a convenience `url` would eventually be added for the
 * review UI, and evidence reads must stay per-request authorized mints (N12).
 *
 * The member's profile answers are **not** here. Staff read them through the
 * members surface, which bypasses the visibility resolver on its own permission
 * (N18) — folding them in would give this endpoint a second authorization rule.
 * `account` is not a breach of that: an email and a lifecycle status are account
 * facts rather than question-engine answers, and they are the four fields every
 * staff work list already names an account by ({@link AccountRefSchema}).
 * Neither is `answerChanges`: on a `re_review` the two values of an edit ARE the
 * thing under review, and judging them is what `verification.review` is (D6).
 */
export const VerificationCaseDetailSchema = z
  .object({
    id: UuidSchema,
    userId: UuidSchema,
    /** Who the member is; `null` when the lookup came back short — see the summary. */
    account: AccountRefSchema.nullable(),
    gender: GenderSchema,
    kind: VerificationCaseKindSchema,
    status: VerificationCaseStatusSchema,
    attemptNumber: z.number().int().positive(),
    outstandingSteps: z.number().int().nonnegative(),
    answerChangeCount: z.number().int().nonnegative(),
    steps: z.array(VerificationStepSchema),
    /** The edit a re-review judges — label, previous, next; empty on an initial case. */
    answerChanges: z.array(AnswerChangeSchema),
    interview: InterviewSchema.nullable(),
    /** The reviewer's private record of the conversation — never in the member's shape. */
    interviewNotes: z.string().nullable(),
    /** Whether the member has paid the interview fee, through which door, and whether it was spent (D9a). */
    interviewFee: InterviewFeeStandingSchema,
    /**
     * The advisory ID-versus-selfie comparison, or `null` when the case has none
     * — a gender whose `verification.steps.faceMatch` is off, or a case not yet
     * submitted.
     *
     * **Staff-only, and that is structural**: `VerificationCaseSchema` has no
     * property for it, because a member who can read their own similarity score
     * can tune a forgery until it passes. It informs a decision; it never makes
     * one (`03-verification.md`).
     */
    faceMatch: FaceMatchCheckSchema.nullable(),
    /**
     * The AI-generated-photo advisories (P27b1) for the case's selfie and the
     * member's current profile photos — one per asset that has been looked at.
     * An asset with no entry has not been checked yet (queued, or the advisory
     * is switched off); an entry with `unavailableReason` was looked at and could
     * not be judged. Staff-only, like `faceMatch`, and for the same reason.
     */
    photoAdvisories: z.array(PhotoAdvisorySchema),
    /**
     * The member's current profile photos, in their own order — the list
     * `photoAdvisories` is keyed against (P27b2). Without it a photo that has
     * NOT been checked is invisible to the reviewer, and "no entry" would read
     * as "nothing to look at" rather than "not yet looked at". Descriptors
     * only: the bytes stay a separate authorized mint (N12).
     */
    memberPhotos: z.array(MemberPhotoSchema),
    decidedByUserId: UuidSchema.nullable(),
    decisionReasonCode: VerificationDecisionReasonSchema.nullable(),
    decisionNote: z.string().nullable(),
    submittedAt: z.iso.datetime().nullable(),
    decidedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
  })
  .strict()
  .superRefine(refineCaseContents);

export type VerificationCaseDetail = z.infer<typeof VerificationCaseDetailSchema>;
